// libraries
import { useRecoilCallback } from 'recoil';

// modules
import {
  FileChangedGroupIdsAtom,
  ParentDataAtom,
  RiskAssessmentDataAtom,
  TransferPhotoDeleteRequestAtom,
  TransferPhotoFileKeyAtom,
  TransferPhotoListAtom
} from 'modules';

// api
import { deleteFiles, postFiles } from '../api/fileApi.js';

export const usePhotoSave = () => {
  const savePhotos = useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        const transferPhotoList = await snapshot.getPromise(TransferPhotoListAtom);
        const transferPhotoDeleteRequest = await snapshot.getPromise(
          TransferPhotoDeleteRequestAtom
        );
        const transferPhotoFileKey = await snapshot.getPromise(TransferPhotoFileKeyAtom);
        const parentData = await snapshot.getPromise(ParentDataAtom);
        const riskAssessmentData = await snapshot.getPromise(RiskAssessmentDataAtom);

        const companyScopeKey = parentData?.company?.scopeKey;
        const projectCode = parentData?.project?.code;
        const originId = riskAssessmentData?.originId;

        const captureImages = transferPhotoList.filter((item) => item.source === 'capture');
        const hadActualChanges =
          (transferPhotoDeleteRequest.length > 0 && !!transferPhotoFileKey) ||
          captureImages.length > 0;

        let uploadedFileKey = transferPhotoFileKey;

        try {
          // 1. 파일 삭제
          if (transferPhotoDeleteRequest.length > 0 && transferPhotoFileKey) {
            await deleteFiles({
              companyScopeKey,
              projectCode,
              fileKey: transferPhotoFileKey,
              savedFileNames: transferPhotoDeleteRequest
            });
            console.debug('=== 파일 삭제 완료 ===');
          }

          // 2. 캡처 이미지 업로드
          if (captureImages.length > 0) {
            const formData = new FormData();
            formData.append('companyScopeKey', companyScopeKey);
            formData.append('projectCode', projectCode);
            if (transferPhotoFileKey) {
              formData.append('fileKey', transferPhotoFileKey);
            }
            captureImages.forEach((item) => {
              formData.append('files', item.file);
            });

            const uploadResponse = await postFiles(formData);
            console.debug('=== 파일 업로드 완료 ===');
            const fileKey = uploadResponse?.data?.fileKey;
            if (fileKey) {
              uploadedFileKey = Number(fileKey);
            }
          }
        } catch (error) {
          console.error('=== 파일 업로드/삭제 실패 ===', error);
          throw error;
        }

        // 3. 사진 관련 상태 초기화 (변경사항 없음 상태로)
        set(TransferPhotoDeleteRequestAtom, []);
        set(TransferPhotoListAtom, (prev) => prev.filter((item) => item.source !== 'capture'));
        if (uploadedFileKey !== transferPhotoFileKey) {
          set(TransferPhotoFileKeyAtom, uploadedFileKey);

          // ParentDataAtom의 markerGroups에도 새 fileKey 반영
          if (originId) {
            set(ParentDataAtom, (prev) => {
              if (!prev?.markerGroups) return prev;
              return {
                ...prev,
                markerGroups: prev.markerGroups.map((group) =>
                  group.id === originId ? { ...group, fileKey: uploadedFileKey } : group
                )
              };
            });
          }
        }

        // 4. 실제 변경이 있었던 경우 FileChangedGroupIdsAtom 업데이트
        if (originId && hadActualChanges) {
          set(FileChangedGroupIdsAtom, (prev) =>
            prev.includes(originId) ? prev : [...prev, originId]
          );
        }

        return uploadedFileKey;
      },
    []
  );

  return { savePhotos };
};
